import React, { useState } from "react";
import { Modal, Autocomplete, TextField, Box, Grid } from "@mui/material";
import { Form, Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import EditIcon from "@mui/icons-material/Edit";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import * as Yup from "yup";

const createModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  "& .MuiFormHelperText-root": { color: "#d32f2f" },
  borderRadius: "0.5rem",
  outline: "none",
};

function CustomizePointGoalModal({ open, handleClose, handleSave, widgetOptions }) {
  const { title, timePeriod, pointGoal } = widgetOptions;
  const [titleEditable, setTitleEditable] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title,
    timePeriod: timePeriod.toLowerCase(),
    pointGoal,
  });
  const [submitLoading, setSubmitLoading] = useState(false);

  const timePeriods = ["Daily", "Weekly", "Monthly", "Quarterly", "Yearly"];

  const validationSchema = Yup.object({
    title: Yup.string()
      .max(30, "Title cannot be more than 30 characters")
      .required("Title is required"),
    pointGoal: Yup.number().required("Point Goal is required"),
  });

  const handleTitleEditClick = () => {
    setTitleEditable(true);
  };

  const handleModalClose = () => {
    if (!submitLoading) {
      setTitleEditable(false);
      handleClose();
    }
  };

  const handleSubmit = async (data) => {
    try {
      setSubmitLoading(true);

      const obj = {
        pointGoal: data.pointGoal,
        title: data.title,
        timePeriod: data.timePeriod,
      };

      handleSave(obj);
      setInitialValues(data);

      setSubmitLoading(false);
      handleModalClose();
    } catch (error) {
      setSubmitLoading(false);
      handleModalClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox
        sx={{
          ...createModalStyle,
          width: {
            xs: "90%",
            md: "600px",
          },
        }}
      >
        <Formik
          enableReinitialize
          validateOnChange
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, touched, errors, dirty }) => (
            <Form>
              <Box sx={{ display: "inline-flex" }}>
                {titleEditable ? (
                  <TextField
                    label="Widget Title"
                    defaultValue={values.title}
                    onChange={(event) => setFieldValue("title", event.target.value)}
                    error={Boolean(touched.title && errors.title)}
                    helperText={touched.title && errors.title}
                    sx={{ mb: "1rem" }}
                    inputProps={{ maxLength: 30 }}
                  />
                ) : (
                  <MDTypography
                    sx={{ mb: "1rem", mr: "0.25rem" }}
                    id="modal-modal-title"
                    variant="h5"
                    component="h2"
                  >
                    {title}
                  </MDTypography>
                )}
                {!titleEditable && (
                  <EditIcon
                    sx={{ cursor: "pointer", position: "relative", top: "3px" }}
                    onClick={() => {
                      handleTitleEditClick();
                    }}
                  />
                )}
              </Box>
              <Grid container sx={{ display: "flex", mb: "1rem" }} spacing={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    disablePortal
                    disableClearable
                    value={
                      (values.timePeriod &&
                        timePeriods.find((option) => option.toLowerCase() === values.timePeriod)) ||
                      null
                    }
                    onChange={(_, value) => setFieldValue("timePeriod", value.toLowerCase())}
                    options={timePeriods}
                    renderInput={(params) => <TextField {...params} label="Display Period" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Point Goal"
                    type="number"
                    value={values.pointGoal}
                    onChange={(e) => {
                      setFieldValue("pointGoal", e.target.value);
                    }}
                    InputProps={{
                      style: {
                        height: "3.25rem",
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: "flex", justifyContent: "space-between", float: "right" }}>
                <Box>
                  <MDButton
                    color="gray80"
                    onClick={handleModalClose}
                    sx={{ mt: 2, mr: 1, position: "relative" }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    color="primaryRed"
                    type="submit"
                    disabled={submitLoading || !dirty}
                    sx={{ mt: 2, position: "relative" }}
                  >
                    {submitLoading ? "Loading..." : "Save"}
                  </MDButton>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </MDBox>
    </Modal>
  );
}

export default CustomizePointGoalModal;
